import { Dialog, Text } from "@flat-ui";
import { usePermission } from "@hooks/use-permission";
import { PermissionKey } from "@utils/permissions/permission-matrix";

interface Props {
  /** The permission the logged in user needs to see the page */
  permission: PermissionKey;

  /** Text for the button leaving the page, e.g. "Go to workspace" */
  leaveText: string;

  /** Called when the user confirms or closes the dialog. It has to navigate away from the page. */
  onLeave(): void;

  /** The page to show to users with the permission */
  children: React.JSX.Element;
}

/**
 * Shows the page only to users whose roles grant the permission.
 * Everyone else gets a dialog that tells them so and takes them away from the page,
 * which keeps the URL of a restricted page from being a way around a disabled button.
 */
export function RequirePermissionPage({
  permission,
  leaveText,
  onLeave,
  children,
}: Props): React.JSX.Element {
  const { isAllowed, deniedReason } = usePermission(permission);

  if (isAllowed) {
    return children;
  }

  return (
    <Dialog
      open={true}
      title="Insufficient role"
      confirmText={leaveText}
      onConfirm={onLeave}
      onClose={onLeave}
    >
      <Text variant="bodyL">
        You don't have the role needed to open this page. {deniedReason}.
      </Text>
    </Dialog>
  );
}
